/**
 * Money, in one place, for every ChiCare client.
 *
 * Build Spec §8: money always crosses the wire as two fields —
 *
 *     { "price_kobo": 45000, "price_display": "₦450" }
 *
 * and "the client never does currency arithmetic". These helpers exist for the
 * cases the server cannot pre-render: a live cart subtotal as the buyer taps
 * the quantity stepper, or a slider in the seller console.
 *
 * Everything here is integer arithmetic on kobo. There is no float in a money
 * path (§9.10) — mirroring App\Support\Kobo on the API side, deliberately,
 * because two implementations of rounding is two answers to "what do I owe".
 */

export const KOBO_PER_NAIRA = 100;

/**
 * Nigerian convention: whole naira carry no decimal tail, and kobo appear only
 * when they are non-zero. "₦450", never "₦450.00".
 */
export function formatKobo(kobo: number): string {
  const value = Math.trunc(kobo);
  const negative = value < 0;
  const absolute = Math.abs(value);

  const naira = Math.floor(absolute / KOBO_PER_NAIRA);
  const remainder = absolute % KOBO_PER_NAIRA;

  let formatted = naira.toLocaleString('en-NG');

  if (remainder !== 0) {
    formatted += `.${String(remainder).padStart(2, '0')}`;
  }

  return `${negative ? '-₦' : '₦'}${formatted}`;
}

/** Parse operator input ("₦1,500", "1500.50") into integer kobo. */
export function koboFromInput(value: string | number): number {
  if (typeof value === 'number') {
    return Math.round(value * KOBO_PER_NAIRA);
  }

  const clean = value.replace(/[^0-9.\-]/g, '');

  if (clean === '' || clean === '-') return 0;

  const negative = clean.startsWith('-');
  const [naira = '0', fraction = ''] = clean.replace('-', '').split('.');

  const kobo =
    Number(naira || 0) * KOBO_PER_NAIRA + Number(fraction.slice(0, 2).padEnd(2, '0') || 0);

  return negative ? -kobo : kobo;
}

/**
 * Percentage in basis points, rounded half-up, staying integral.
 * 5% of ₦2,500 → koboBps(250000, 500) → 12500 kobo (₦125).
 */
export function koboBps(kobo: number, basisPoints: number): number {
  return Math.trunc((kobo * basisPoints + 5_000) / 10_000);
}

export function sumKobo(values: readonly number[]): number {
  return values.reduce((total, value) => total + Math.trunc(value), 0);
}

/**
 * A money pair as the API sends it. Prefer rendering `display` — it is what the
 * server decided, and it is what the seller's statement will also say.
 */
export interface Money {
  kobo: number;
  display: string;
}

export function money(kobo: number): Money {
  return { kobo, display: formatKobo(kobo) };
}
